import { AppConstants } from "../../constants/AppConstants";
import environment from "../../environment";
import {
  capitalize,
  cloneDeep,
  find,
  findIndex,
  initial,
  isEmpty,
  isObject,
  isUndefined,
  last,
  replace,
  split,
  startCase,
  toLower,
} from "lodash";
import {
  createSignedURL,
  getGCPFile,
  uploadFile,
} from "../../http/asset-management-services";
import { HTTP_STATUS } from "../../http/constants/http.status";

export const getFileName = (filePath: string) => {
  const parts = split(filePath, ".");
  if (parts.length > 1) {
    return initial(parts).join(".");
  }
  return filePath;
};

export const getFileFormat = (fileName: string) => {
  const parts = split(fileName, ".");
  if (parts.length > 1) {
    return toLower(last(parts));
  }
  return "";
};

export const findAssetFormat = (options: Array<any>, key: string) => {
  let obj = find(options, { key });
  return obj ? Object.keys(obj.allowedTypes).join(",") : "";
};

export const getGlobalBucketName = () => {
  const nameSpace =
    window.sessionStorage.getItem("namespace") || environment.namespace;
  let bucketName = `${nameSpace.split("-").slice(-2).join("-")}-`;
  return `${bucketName}${AppConstants.ASSET_MANAGEMENT_SERVICE}`;
};

export const updateThemeFile = async (jsonData: string, userId: string) => {
  const key = AppConstants.ADMIN_THEME_PACK;
  return new Promise(async (resolve) => {
    const blob = new Blob([jsonData], { type: AppConstants.VALUE_TEXT_PLAIN });
    const file = new File([blob], key);

    const payload = {
      filePath: `${AppConstants.getGCPPath()}${AppConstants.ADMIN_THEME_PACK}`,
      bucketName: getGlobalBucketName(),
      versioning: true,
      action: AppConstants.GCS_UPLOAD_ACTION,
      publicObject: true,
      expirationTime: AppConstants.GCS_MAX_EXPIRATION_TIME,
      metadata: {
        fileName: file.name,
        key: key,
        userId: userId,
        [AppConstants.HEADER_CACHE_CONTROL]: AppConstants.VALUE_NO_CACHE,
        [AppConstants.HEADER_MAX_AGE]: AppConstants.VALUE_MAX_AGE_MINIMUM,
      },
    };

    const uploadResponse = await createSignedURL(payload);
    const { status, data } = uploadResponse;
    if (HTTP_STATUS.isSuccess(status)) {
      const { url, headers = {} } = data;
      const uploadPayload = {
        url,
        body: file,
        headers: { ...headers, [AppConstants.HEADER_CONTENT_TYPE]: file.type },
      };
      const response = await uploadFile(
        uploadPayload,
        () => {},
        (err: string) => {
          console.error(err);
        }
      );
      resolve({ ...response });
    } else {
      resolve({ ...uploadResponse });
    }
  });
};

export const getPropertyNames = (colorKey: string) => {
  const words = colorKey
    .replace(/([A-Z])/g, " $1")
    .trim()
    .split(" ");
  const capitalizedName = words
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
  return capitalizedName;
};

const getCustomizationOptions = () => [
  {
    key: "bannerLogo",
    display: "T_CUSTOMIZATION_OPTION_BANNER_LOGO",
    allowedTypes: {
      "image/x-icon": [".ico"],
    },
    showPreview: true,
    url: AppConstants.getLogoIconUrl(),
    fileName: AppConstants.SOLUTION_BANNER_ICON,
    description: "T_ASSET_LOGO_DESCRIPTION",
    localKey: "useLocalLogo",
  },
  {
    key: "bannerImg",
    display: "T_CUSTOMIZATION_OPTION_BANNER_IMG",
    allowedTypes: {
      "image/png": [".png"],
    },
    showPreview: true,
    url: AppConstants.getLogoSolutionUrl(),
    fileName: AppConstants.SOLUTION_BANNER_IMG,
    description: "T_ASSET_BANNER_DESCRIPTION",
    localKey: "useLocalBanner",
  },
  {
    key: "splashImg",
    display: "T_CUSTOMIZATION_OPTION_SPLASH_IMG",
    allowedTypes: {
      "image/png": [".png"],
    },
    showPreview: true,
    url: AppConstants.getLogoSplashUrl(),
    fileName: AppConstants.ADMIN_SPLASH_IMG,
    description: "T_ASSET_SPLASH_DESCRIPTION",
    localKey: "useLocalSplash",
  },
];

export const customizationOptions = getCustomizationOptions();

export const getLanguageFileName = (path: string) => {
  const lastSlashIndex = path.lastIndexOf("/");
  if (lastSlashIndex === -1) {
    return path;
  }
  return path.substring(lastSlashIndex + 1);
};

export const getLanguageName = (obj: any) => {
  const { metadata = {} } = obj;
  const { filename = {}, languagename = "" } = metadata;

  return languagename || filename;
};

export const isValidLocale = (locale: string) => {
  // const localePattern = /^[a-z]{2,3}(-[A-Z]{2,3})?$/;
  // return localePattern.test(locale);
  return true;
};

export const updateLocalization = async (
  languageFile: any,
  userId: string,
  languageName: string
) => {
  return new Promise(async (resolve, reject) => {
    let key = Object.keys(languageFile)[0];
    console.log(key)
    let fileName = getLanguageFileName(key);
    const payload = {
      filePath: key,
      bucketName: getGlobalBucketName(),
      versioning: true,
      action: AppConstants.GCS_UPLOAD_ACTION,
      publicObject: true,
      expirationTime: AppConstants.GCS_MAX_EXPIRATION_TIME,
      metadata: {
        fileName,
        languageName,
        key: fileName,
        userId,
        [AppConstants.HEADER_CACHE_CONTROL]: AppConstants.VALUE_NO_CACHE,
        [AppConstants.HEADER_MAX_AGE]: AppConstants.VALUE_MAX_AGE_MINIMUM,
      },
    };
    const signedUrlResponse = await createSignedURL(payload);
    const { status, data } = signedUrlResponse;
    if (data) {
      const { url, headers = {}, issues = [] } = data;
      if (status == HTTP_STATUS.HTTP_CREATED) {
        let [fileObj] = languageFile?.[key];
        const uploadPayload = {
          url,
          body: fileObj,
          headers: {
            ...headers,
            [AppConstants.HEADER_CONTENT_TYPE]: fileObj?.type,
          },
        };

        let uploadFileRes = await uploadFile(uploadPayload);
        if (uploadFileRes.status === HTTP_STATUS.HTTP_OK) {
          resolve(uploadFileRes);
        } else {
          reject({
            status: uploadFileRes.status,
          });
        }
      } else if (status == HTTP_STATUS.HTTP_BAD_REQUEST) {
        reject({
          status,
        });
      }
    }
  });
};

export function isValidUrl(string: string) {
  try {
    new URL(string);
    return true;
  } catch (err) {
    return false;
  }
}

export const updatePrivacyUrl = async (privacyUrl: any, userId: string) => {
  let jsonData: any = {};

  try {
    let response = await getGCPFile(
      AppConstants.getConfigFullPath()
    );

    const { data, status } = response;
    if (
      !isUndefined(data) &&
      !isEmpty(data) &&
      HTTP_STATUS.isSuccess(status) &&
      isObject(data)
    ) {
      jsonData = cloneDeep(data);
    }
  } catch (e) {
    console.log(e);
  }
  jsonData.privacyUrl = privacyUrl;

  return new Promise(async (resolve) => {
    const blob = new Blob([JSON.stringify(jsonData, null, "\t")], {
      type: AppConstants.VALUE_APPLICATION_JSON,
    });
    const file = new File(
      [blob],
      AppConstants.DATA_REQUEST_APP_CONFIG_FILE_NAME
    );
    const ConfigPath = AppConstants.getGCPPath() + "config.json";
    const payload = {
      filePath: ConfigPath,
      bucketName: getGlobalBucketName(),
      versioning: true,
      action: AppConstants.GCS_UPLOAD_ACTION,
      publicObject: true,
      expirationTime: AppConstants.GCS_MAX_EXPIRATION_TIME,
      metadata: {
        userId: userId,
        [AppConstants.HEADER_CACHE_CONTROL]: AppConstants.VALUE_NO_CACHE,
        [AppConstants.HEADER_MAX_AGE]: AppConstants.VALUE_MAX_AGE_MINIMUM,
      },
    };

    const uploadResponse = await createSignedURL(payload);
    const { status, data } = uploadResponse;
    if (status === HTTP_STATUS.HTTP_CREATED) {
      const { url, headers = {} } = data;
      const uploadPayload = {
        url,
        body: file,
        headers: {
          ...headers,
          [AppConstants.HEADER_CONTENT_TYPE]:
            AppConstants.VALUE_APPLICATION_JSON,
        },
      };
      const response = await uploadFile(
        uploadPayload,
        () => {},
        (err: string) => {
          console.error(err);
        }
      );
      resolve({ ...response });
    } else {
      resolve({ ...uploadResponse });
    }
  });
};

export const updateTranslationObj = async (
  languageFile: any,
  userId: string,
  languagename: string
) => {
  let key = Object.keys(languageFile)[0];
  let filename = getLanguageFileName(key);

  let jsonData: any = {};

  try {
    let response = await getGCPFile(
      AppConstants.getConfigFullPath()
    );
    const { data, status } = response;
    if (
      !isUndefined(data) &&
      !isEmpty(data) &&
      HTTP_STATUS.isSuccess(status) &&
      isObject(data)
    ) {
      jsonData = cloneDeep(data);
    }

    if (jsonData?.languages?.length) {
      let index = findIndex(
        jsonData?.languages,
        (item: any) => item?.filename === filename
      );

      if (index !== -1) {
        return;
      }
    }
  } catch (e) {
    console.log(e);
  }
  if (jsonData?.languages) {
    jsonData.languages.push({
      filename,
      languagename,
    });
  } else {
    jsonData.languages = [
      {
        filename,
        languagename,
      },
    ];
  }

  return new Promise(async (resolve) => {
    const blob = new Blob([JSON.stringify(jsonData, null, "\t")], {
      type: AppConstants.VALUE_APPLICATION_JSON,
    });
    const file = new File([blob], key);
    const ConfigPath = AppConstants.getGCPPath() + "config.json";
    const payload = {
      filePath: ConfigPath,
      bucketName: getGlobalBucketName(),
      versioning: true,
      action: AppConstants.GCS_UPLOAD_ACTION,
      publicObject: true,
      expirationTime: AppConstants.GCS_MAX_EXPIRATION_TIME,
      metadata: {
        userId: userId,
        [AppConstants.HEADER_CACHE_CONTROL]: AppConstants.VALUE_NO_CACHE,
        [AppConstants.HEADER_MAX_AGE]: AppConstants.VALUE_MAX_AGE_MINIMUM,
      },
    };

    const uploadResponse = await createSignedURL(payload);
    const { status, data } = uploadResponse;
    if (status === HTTP_STATUS.HTTP_CREATED) {
      const { url, headers = {} } = data;
      const uploadPayload = {
        url,
        body: file,
        headers: {
          ...headers,
          [AppConstants.HEADER_CONTENT_TYPE]:
            AppConstants.VALUE_APPLICATION_JSON,
        },
      };
      const response = await uploadFile(
        uploadPayload,
        () => {},
        (err: string) => {
          console.error(err);
        }
      );
      resolve({ ...response });
    } else {
      resolve({ ...uploadResponse });
    }
  });
};
